import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  TextField,
  MenuItem,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import PublicIcon from '@mui/icons-material/Public';
import { CircularProgress, Alert } from '@mui/material';
import { REPS_COLORS, oeBand } from '../../utils/constants';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { useSiteComparisons } from '../../hooks/useSiteComparisons';
import OrgSwitcher from '../../components/OrgSwitcher';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';

/**
 * Regional Rollup — Mindmap branch 2, "Regional Rollup"
 * LIVE: regions and site scores come from the active organization's data.
 */
function RegionalRollup() {
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const { comparisons, loading } = useSiteComparisons(organization?.id || null);
  const { queryScope } = useDemoRole();
  const [regionId, setRegionId] = useState('');

  // Site-scoped viewers (e.g. a DH) are pinned to their own site's region.
  const lockedRegionId = queryScope?.siteId
    ? comparisons?.siteScores?.find((s) => s.siteId === queryScope.siteId)?.regionId || null
    : null;

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }
  if (!comparisons || comparisons.regions.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <PublicIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" sx={{ flexGrow: 1 }}>Regional Rollup</Typography>
          <OrgSwitcher />
        </Box>
        <Alert severity="info">
          The regional rollup appears once your organization has site-level survey data.
        </Alert>
      </Box>
    );
  }

  const activeRegionId = lockedRegionId || regionId || comparisons.regions[0].id;
  const region = comparisons.regions.find((r) => r.id === activeRegionId) || comparisons.regions[0];
  const siteScores = region.sites;
  const sitesInRegion = siteScores;
  const regionalAvg = comparisons.regionalAverages[region.id];

  const chartData = siteScores.map((s) => ({
    name: s.siteName,
    Reliability: s.scores.reliability,
    Efficiency: s.scores.efficiency,
    Productivity: s.scores.productivity,
    Safety: s.scores.safety,
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <PublicIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Regional Rollup</Typography>
        <TextField
          select
          size="small"
          label="Region"
          value={activeRegionId}
          onChange={(e) => setRegionId(e.target.value)}
          sx={{ minWidth: 220 }}
          disabled={Boolean(lockedRegionId)}
          helperText={lockedRegionId ? 'Scoped to your region' : undefined}
        >
          {(lockedRegionId
            ? comparisons.regions.filter((r) => r.id === lockedRegionId)
            : comparisons.regions
          ).map((r) => (
            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
          ))}
        </TextField>
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        <strong>{region?.name}</strong> spans {sitesInRegion.length} site{sitesInRegion.length !== 1 ? 's' : ''}. Compare site-by-site against the regional average to flag outliers.
      </Typography>

      <Grid container spacing={3}>
        {regionalAvg && Object.entries(regionalAvg).map(([k, v]) => {
          const repsColor = REPS_COLORS[k] || '#666';
          const band = oeBand(v, organization?.thresholds, k);
          return (
            <Grid item xs={6} md={3} key={k}>
              <Card sx={{ borderTop: 4, borderTopColor: repsColor }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', color: repsColor, fontWeight: 700, lineHeight: 1.2 }}>{k}</Typography>
                  <Typography variant="h3" fontWeight={700} sx={{ color: band.color }}>{v.toFixed(1)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Regional avg</Typography>
                    <Typography variant="caption" sx={{ color: band.color, fontWeight: 700 }}>{band.label}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={v * 10}
                    sx={{
                      height: 6, borderRadius: 3, mt: 0.5,
                      backgroundColor: 'rgba(0,0,0,0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: band.color },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Sites in Region</Typography>
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Reliability" fill={REPS_COLORS.reliability} />
                    <Bar dataKey="Efficiency" fill={REPS_COLORS.efficiency} />
                    <Bar dataKey="Productivity" fill={REPS_COLORS.productivity} />
                    <Bar dataKey="Safety" fill={REPS_COLORS.safety} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Site Trend Summary</Typography>
              <Stack spacing={1.5}>
                {siteScores.map((s) => {
                  const avg = (s.scores.reliability + s.scores.efficiency + s.scores.productivity + s.scores.safety) / 4;
                  return (
                    <Box key={s.siteId} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={600}>{s.siteName}</Typography>
                        <Chip
                          label={s.trend}
                          size="small"
                          color={s.trend === 'improving' ? 'success' : s.trend === 'worsening' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Avg <strong>{avg.toFixed(1)}</strong> • {s.basis || `Responses ${s.lastSurveyResponses}/${s.targetResponses}`}{s.lowConfidence ? ' ⚠' : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default RegionalRollup;
